import type { FC } from "react";
import { useTranslation } from "@privacy/translations";
import { useForm } from "react-hook-form";
import * as FormDialog from "../../shared/FormDialog";
import type { CreateSystemPayload } from "../../api/systems.types";
import { useCreateSystem } from "../../api/systems";

type CreateSystemDialogProps = {
  onClose: () => void;
  onCreated: (system: { name: string; id: string }) => void;
  defaultName?: string;
};

export const CreateSystemDialog: FC<CreateSystemDialogProps> = ({ onClose, onCreated, defaultName = "" }) => {
  const { t } = useTranslation();

  const { mutate: createSystem, isError, isPending: isSubmitting } = useCreateSystem();
  const { handleSubmit, reset, control, setFocus } = useForm<CreateSystemPayload>({
    defaultValues: { name: defaultName },
  });

  FormDialog.useAutoFocusField(setFocus, "name");

  const handleNewSystem = ({ name }: { name: string }) =>
    createSystem(
      { name },
      {
        onSuccess: async ({ id }) => {
          onClose();
          reset();
          onCreated({ name, id });
        },
      }
    );

  const handleCancel = () => {
    onClose();
    reset();
  };

  return (
    <FormDialog.Dialog isOpen={true}>
      <FormDialog.Title>{t("CreateSystem.Dialog.Title")}</FormDialog.Title>

      <FormDialog.Form onSubmit={handleSubmit(handleNewSystem)}>
        <FormDialog.Content>
          <FormDialog.FormError isError={isError} />
          <FormDialog.Fields>
            <FormDialog.TextField
              control={control}
              name="name"
              rules={{ required: t("CreateSystem.Dialog.Error") }}
              label={t("CreateSystem.Dialog.NameLabel")}
              placeholder={t("CreateSystem.Dialog.NamePlaceholder")}
            />
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={handleCancel} />
          <FormDialog.SubmitButton isSubmitting={isSubmitting}>
            {t("CreateSystem.Dialog.SubmitButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
